import AccessUser from "./AccessUser";
import storeUtils from "./../store/storeUtils";
import cacheService from "@/services/cache";
import { APPLICATION } from "@/components/admin/brand/constant";

export const PROFILES = Object.freeze({
    FORECAST_MANAGER: "FORECAST_MANAGER",
    ZONE_READER: "ZONE_READER",
    ZONE_FORECASTER: "ZONE_FORECASTER",
    ZONES_SUPERVISOR: "ZONES_SUPERVISOR",
    COORDINATOR: "COORDINATOR"
});

class AccessUserPlugin {
    constructor() {
        this.functionalities = null;
        this.access = null;
        this.accessBrand = null;
        this.applications = null;
        this.user = null;
        this.roles = null;
        this.load = false;
        this.type = null;
        this.profiles = null;
        if (cacheService.getRem3rchUser()) {
            let user = cacheService.getRem3rchUser();
            this.type = user.user.type;
            this.init();
        }
    }

    async init() {
        return await Promise.all([
            this.promiseAccessUser(),
            this.promiseFunctionalities(),
            this.promiseAccessBrand(),
            this.promiseAllApplications(),
            this.promiseUpdateUserRoles()
        ]);
    }

    reInit() {
        this.clearCache();
        return this.init();
    }

    clearCache() {
        cacheService.clearOnReload();
    }

    promiseUpdateUserRoles() {
        return new Promise(resolve => {
            let roles = cacheService.getUserRoles();
            if (!roles) {
                storeUtils
                    .actions("roles/findAllRoles")
                    .then(() => {
                        roles = storeUtils.getters("roles/getRoles");
                        this.setRoles(roles);
                        cacheService.setRem3rchUserRoles(roles);
                        let profiles = storeUtils.getters("roles/getProfiles");
                        this.setProfiles(profiles);
                        cacheService.setRem3rchUserProfiles(profiles);
                        resolve(this);
                    })
                    .catch(e => console.log(e));
            } else if (this.roles === null) {
                resolve(this);
            } else {
                resolve(this);
            }
        });
    }

    promiseAllApplications() {
        return new Promise(resolve => {
            let applications = storeUtils.getters("functionality/getAllApplications");
            if (!applications) {
                applications = cacheService.getCacheObject("all_applications");
                if (!applications) {
                    storeUtils.actions("functionality/findAllApplications").then(() => {
                        cacheService.addCacheObject("all_applications", storeUtils.getters("functionality/getAllApplications"));
                        this.setApplications(storeUtils.getters("functionality/getAllApplications"));
                        resolve(this);
                    });
                } else {
                    this.setApplications(applications);
                    resolve(this);
                }
            } else if (this.applications === null) {
                this.setApplications(applications);
                resolve(this);
            } else {
                resolve(this);
            }
        });
    }

    promiseAccessBrand() {
        return new Promise(resolve => {
            let accessBrand = storeUtils.getters("brand/getBrandAccess");
            if (!accessBrand) {
                accessBrand = cacheService.getCacheObject("access_brand");
                if (!accessBrand) {
                    storeUtils.actions("brand/findBrandAccess", cacheService.getCurrentBrandId()).then(() => {
                        cacheService.addCacheObject("access_brand", storeUtils.getters("brand/getBrandAccess"));
                        this.setAccessBrand(storeUtils.getters("brand/getBrandAccess"));
                        resolve(this);
                    });
                } else {
                    this.setAccessBrand(accessBrand);
                    resolve(this);
                }
            } else if (this.accessBrand === null) {
                this.setAccessBrand(accessBrand);
                resolve(this);
            } else {
                resolve(this);
            }
        });
    }

    promiseFunctionalities() {
        return new Promise(resolve => {
            let functionalities = storeUtils.getters("functionality/getAllFunctionalitiesLight");
            if (!functionalities) {
                functionalities = cacheService.getCacheObject("functionalities");
                if (!functionalities) {
                    storeUtils.actions("functionality/findAllFunctionalities").then(() => {
                        cacheService.addCacheObject("functionalities", storeUtils.getters("functionality/getAllFunctionalitiesLight"));
                        this.setFunctionalities(storeUtils.getters("functionality/getAllFunctionalitiesLight"));
                        resolve(this);
                    });
                } else {
                    this.setFunctionalities(functionalities);
                    resolve(this);
                }
            } else if (this.functionalities === null) {
                this.setFunctionalities(functionalities);
                resolve(this);
            } else {
                resolve(this);
            }
        });
    }

    promiseAccessUser() {
        return new Promise(resolve => {
            let access = storeUtils.getters("roles/getAllAccess");
            if (!access) {
                access = cacheService.getCacheObject("all_access");
                if (!access) {
                    storeUtils.actions("roles/findAllRolesComplet").then(() => {
                        this.setAccess(storeUtils.getters("roles/getAllAccess"));
                        cacheService.addCacheObject("all_access", storeUtils.getters("roles/getAllAccess"));
                        resolve(this);
                    });
                } else {
                    this.setAccess(access);
                    resolve(this);
                }
            } else if (this.access === null) {
                this.setAccess(access);
                resolve(this);
            } else {
                resolve(this);
            }
        });
    }

    getApplications() {
        return this.applications;
    }

    setApplications(applications) {
        this.applications = applications;
    }

    getAccessBrand() {
        return this.accessBrand;
    }

    setAccessBrand(accessBrand) {
        this.accessBrand = accessBrand;
    }

    getCurrentBrand() {
        return cacheService.getBrands().find(b => b.id === cacheService.getCurrentBrandId());
    }

    getBrandById(brandId) {
        return cacheService.getBrands().find(b => b.id === brandId);
    }

    isClientAdmin(brandId = null) {
        const currentBrand = !brandId ? this.getCurrentBrand() : this.getBrandById(brandId);
        if (currentBrand && currentBrand.userBrand) {
            return currentBrand.userBrand.isClientAdmin === true;
        }
        return false;
    }

    getRoles() {
        if (this.roles === null) {
            this.roles = cacheService.getUserRoles();
        }
        return this.roles;
    }

    getProfiles() {
        if (this.profiles === null) {
            this.profiles = cacheService.getUserProfiles();
        }
        return this.profiles;
    }

    setRoles(value) {
        this.roles = value;
    }

    setProfiles(value) {
        this.profiles = value;
    }

    getFunctionalities() {
        if (this.functionalities === null) {
            this.functionalities = cacheService.getCacheObject("functionalities");
        }
        return this.functionalities;
    }

    setFunctionalities(functionalities) {
        this.functionalities = functionalities;
    }

    getAccess() {
        if (this.access === null) {
            this.access = cacheService.getCacheObject("all_access");
        }
        return this.access;
    }

    setAccess(access) {
        this.access = access;
    }

    isSuperAdmin() {
        let roles = this.getRoles();
        if (!roles) return false;
        if (roles.hasOwnProperty(AccessUser.SUPPER_ADMIN)) {
            return true;
        }

        return false;
    }

    hasAccessApplicationByFunctionality(functionalityName) {
        let accessesBrand = this.accessBrand;
        if (functionalityName === null) return true;
        if (!Array.isArray(this.applications)) return false;
        let applis = this.applications.filter(appli => {
            return appli.functionalities.some(funct => {
                return funct.name === functionalityName;
            });
        });
        // debugger;
        if (applis.length > 1) {
            throw new Error(`The functionality ${functionalityName} is present is multiple applications: ${applis}`);
        }
        let app = applis[0];

        let accessBrand = accessesBrand.filter(access => {
            return access.application === app.id;
        });

        return accessBrand.length > 0;
    }

    /**
     * Prend actions comme parametre et non roles car les roles sont dynamiques
     */
    hasAccess(functionality, appsId = [6], ...actions) {
        if (!this.getRoles()) return false;
        if (this.isSuperAdmin()) return true;

        if (!functionality) return false;
        if (this.hasAccessApplicationByFunctionality(functionality)) {
            if (this.getFunctionalities().includes(functionality)) {
                if (this._canAccess(functionality, appsId, ...actions)) {
                    return true;
                }
            }
        }

        return false;
    }

    hasAccessByType(type, functionality, appsId = [6], ...actions) {
        if (this.type === null)
            throw Error(
                "User type is undefined. " +
                    "This is because we don't vue binding up on roles nor their are defined on the stores. So we have to " +
                    "prevent roles from being checked before we fetched the user"
            );
        if (this.isSuperAdmin()) return true;
        if (type !== this.type) return false;
        return this.hasAccess(functionality, appsId, ...actions);
    }

    hasAccessByProfile(profile, appId = 6) {
        if (this.isSuperAdmin()) return true;

        appId = appId ? appId : APPLICATION.FORECAST_ID;
        let profiles = this.getProfiles();
        return profiles.some(p => p.profileName === profile && p.id === appId);
    }

    _canAccess(functionality, appsId = [6], ...actions) {
        let error = 0;
        let actionsByFun = this.getActionByFunctionality(functionality, appsId);
        actionsByFun.forEach(actionByFun => {
            for (const action of actions) {
                if ((actionByFun & action) === 0) {
                    error++;
                }
            }
        });

        return error !== actionsByFun.length;
    }

    getActionByFunctionality(functionality, appsId = [6]) {
        let roles = this.getRoles();
        let actionsByRole = [];
        if (!roles) return actionsByRole;
        if (!appsId.some(appId => roles.hasOwnProperty(appId))) return actionsByRole;
        appsId.forEach(appId => {
            if (roles.hasOwnProperty(appId)) {
                if (roles[appId].hasOwnProperty(functionality)) {
                    let role = roles[appId][functionality];
                    let rolesDispo = this.getAccess();
                    if (rolesDispo.hasOwnProperty(role)) {
                        actionsByRole.push(rolesDispo[role]);
                    }
                }
            }
        });

        return actionsByRole;
    }
}

export default function(Vue) {
    Vue.accessUser = new AccessUserPlugin();
    Object.defineProperties(Vue.prototype, {
        $accessUser: {
            get: function() {
                return Vue.accessUser;
            }
        }
    });
}



// WEBPACK FOOTER //
// ./src/plugins/AccessUserPlugin.js